/**
 * Simulador de pedido de capital (Mati, 2026-10-18): un monto objetivo en USD se reparte por el % vigente de cada
 * fiduciante (sus clases A y B sumadas) y cada parte se redondea para arriba a un paso (USD 1.000 por defecto), con
 * ajustes de a un paso por fiduciante para pedir números redondos. No guarda nada hasta que el admin arma el llamado con
 * esos montos (`aPayloadLlamadoAMano`, el backend lo guarda con `montos_a_mano`); los aportes se cargan aparte.
 * Con los saldos de las cuentas corrientes (Mati, 2026-10-19, casilla marcada por defecto) lo que se redondea es lo
 * "a cubrir" de cada uno: su parte + lo que debe − lo que tiene a favor, nunca menos de 0 (si el a favor pasa su parte
 * se le pide 0 y el resto le sigue quedando a favor; no se les baja a los demás). Montos en centavos (`BigInt`), porcentajes escalados a 10^4 (`lib/participacion.ts`): nunca float.
 */
import type { Distribucion } from "./api";
import type { FormLlamado, PayloadLlamado } from "./aportes";
import { formatMoneda } from "./format";
import { aCentavos, deCentavos, parseMonto } from "./gastos";
import { aEscalado, aTextoApi, formatParticipacion } from "./participacion";

export interface ParteFiduciante {
  fiduciante_id: number;
  nombre: string;
  porcentaje: number; // escalado a 10^4, sus clases sumadas
}

/** Un fiduciante por línea (A y B sumadas), de mayor a menor %, a igual % por nombre. */
export function partesDe(distribucion: Distribucion | null): ParteFiduciante[] {
  const porId = new Map<number, ParteFiduciante>();
  for (const item of distribucion?.items ?? []) {
    const pct = aEscalado(item.porcentaje) ?? 0;
    const previa = porId.get(item.fiduciante_id);
    if (previa) previa.porcentaje += pct;
    else porId.set(item.fiduciante_id, { fiduciante_id: item.fiduciante_id, nombre: item.nombre, porcentaje: pct });
  }
  return [...porId.values()].sort((a, b) => b.porcentaje - a.porcentaje || a.nombre.localeCompare(b.nombre, "es"));
}

/** n / d con redondeo bancario (mitad al par), enteros no negativos. */
function dividirBancario(n: bigint, d: bigint): bigint {
  const q = n / d;
  const doble = (n % d) * 2n;
  return doble > d || (doble === d && q % 2n === 1n) ? q + 1n : q;
}

/** Parte exacta de cada uno en centavos, como el reparto de un llamado: redondeo bancario y la diferencia a la de
 * mayor % (la primera, por el orden de `partesDe`), así la suma da el monto. */
export function repartirJusto(montoCentavos: bigint, partes: ParteFiduciante[]): bigint[] {
  const total = partes.reduce((s, p) => s + BigInt(p.porcentaje), 0n);
  if (total === 0n) return partes.map(() => 0n);
  const lineas = partes.map((p) => dividirBancario(montoCentavos * BigInt(p.porcentaje), total));
  lineas[0] = (lineas[0] ?? 0n) + montoCentavos - lineas.reduce((s, l) => s + l, 0n);
  return lineas;
}

/** Pasos de redondeo, en dólares enteros. */
export const PASOS = [100, 500, 1_000, 5_000, 10_000] as const;
export const PASO_INICIAL = 1_000;
export const OPCIONES_PASO = PASOS.map((p) => ({ valor: String(p), etiqueta: formatMoneda(p, "USD").replace(/,00$/, "") }));

/** Redondea para arriba al múltiplo de `paso` (centavos). 0 queda 0. */
export function redondearArriba(centavos: bigint, paso: bigint): bigint {
  return ((centavos + paso - 1n) / paso) * paso;
}

export interface FilaPedido extends ParteFiduciante {
  justo: string; // decimal "16257.00"
  saldo: string | null; // de la cuenta corriente (> 0 a favor, < 0 debe); null si no se tienen en cuenta
  aCubrir: string; // justo − saldo, nunca debajo de 0 (sin saldos, = justo)
  pedido: string; // decimal, redondo
  sobre: string; // pedido − aCubrir (puede ser negativo si se bajó)
  ajuste: number; // pasos sumados (o restados) a mano sobre el redondeo
  puedeBajar: boolean;
}

export interface Pedido {
  filas: FilaPedido[];
  objetivo: string;
  aCubrir: string; // Σ aCubrir: objetivo + lo que deben − lo que se descuenta de los a favor
  deben: string; // Σ de los saldos negativos, sin signo
  aFavorDescontado: string; // Σ de lo a favor que se descontó (hasta la parte de cada uno)
  conSaldos: boolean;
  total: string;
  diferencia: string; // total − aCubrir
}

/**
 * Arma el pedido: parte justa, menos el saldo de la cuenta corriente si vienen `saldos` (decimales con signo por
 * fiduciante_id; el que no está, al día), redondeo para arriba a `paso` dólares y `ajustes[fiduciante_id]` pasos más o
 * menos (nunca debajo de 0). null sin un monto válido o sin fiduciantes.
 */
export function armarPedido(
  montoTexto: string,
  partes: ParteFiduciante[],
  paso: number,
  ajustes: Record<number, number>,
  saldos: Record<number, string> | null = null,
): Pedido | null {
  const monto = parseMonto(montoTexto);
  if (monto === null || partes.length === 0) return null;
  const objetivo = aCentavos(monto);
  const pasoC = BigInt(paso) * 100n;
  const justos = repartirJusto(objetivo, partes);
  let total = 0n;
  let aCubrirTotal = 0n;
  let deben = 0n;
  let aFavorDescontado = 0n;
  const filas = partes.map((p, i) => {
    const justo = justos[i] ?? 0n;
    const saldo = saldos ? aCentavosConSigno(saldos[p.fiduciante_id] ?? "0") : 0n;
    if (saldo < 0n) deben -= saldo;
    else aFavorDescontado += saldo < justo ? saldo : justo;
    const aCubrir = justo - saldo > 0n ? justo - saldo : 0n;
    const ajuste = ajustes[p.fiduciante_id] ?? 0;
    let pedido = redondearArriba(aCubrir, pasoC) + BigInt(ajuste) * pasoC;
    if (pedido < 0n) pedido = 0n;
    total += pedido;
    aCubrirTotal += aCubrir;
    return {
      ...p,
      justo: deCentavos(justo),
      saldo: saldos ? conSigno(saldo) : null,
      aCubrir: deCentavos(aCubrir),
      pedido: deCentavos(pedido),
      sobre: conSigno(pedido - aCubrir),
      ajuste,
      puedeBajar: pedido > 0n,
    };
  });
  return {
    filas,
    objetivo: deCentavos(objetivo),
    aCubrir: deCentavos(aCubrirTotal),
    deben: deCentavos(deben),
    aFavorDescontado: deCentavos(aFavorDescontado),
    conSaldos: saldos !== null,
    total: deCentavos(total),
    diferencia: conSigno(total - aCubrirTotal),
  };
}

const conSigno = (c: bigint): string => (c < 0n ? `-${deCentavos(-c)}` : deCentavos(c));
const aCentavosConSigno = (decimal: string): bigint => (decimal.startsWith("-") ? -aCentavos(decimal.slice(1)) : aCentavos(decimal));

/** "Suma USD 30.000 que deben y descuenta USD 36.257 a favor: a cubrir USD 93.743." · null sin saldos. */
export function detalleSaldos(pedido: Pedido): string | null {
  if (!pedido.conSaldos) return null;
  const partes = [
    pedido.deben !== "0.00" ? `suma ${formatUsd(pedido.deben)} que deben` : null,
    pedido.aFavorDescontado !== "0.00" ? `descuenta ${formatUsd(pedido.aFavorDescontado)} a favor` : null,
  ].filter((t) => t !== null);
  const como = partes.length > 0 ? partes.join(" y ") : "nadie debe ni tiene a favor";
  return `${como.charAt(0).toUpperCase()}${como.slice(1)}: a cubrir ${formatUsd(pedido.aCubrir)}.`;
}

/** "USD 3.743 más de lo justo" · "USD 200 menos" · "Justo" (sin centavos si son cero). */
export function mensajeDiferencia(diferencia: string, cuanto = "de lo justo"): string {
  if (/^-?0\.00$/.test(diferencia)) return "Justo";
  const negativo = diferencia.startsWith("-");
  const texto = `${formatUsd(negativo ? diferencia.slice(1) : diferencia)} ${negativo ? "menos" : "más"}`;
  return cuanto ? `${texto} ${cuanto}` : texto;
}

/** "USD 20.000" (sin ",00"), "USD 16.257,50". */
export const formatUsd = (decimal: string): string => formatMoneda(decimal, "USD").replace(/,00$/, "");

/** Lo que paga cada uno sobre el total pedido (escalado a 10^4), para ver cuánto se corre del % de participación. */
export function porcentajeSobreTotal(fila: FilaPedido, total: string): string {
  const t = aCentavos(total);
  if (t === 0n) return "—";
  return formatParticipacion(aTextoApi(Number(dividirBancario(aCentavos(fila.pedido) * 1_000_000n, t))));
}

const celdaCsv = (texto: string): string => (/[";\n]/.test(texto) ? `"${texto.replace(/"/g, '""')}"` : texto);
const numeroCsv = (decimal: string): string => decimal.replace(".", ",");

/** CSV para Excel en castellano (separador `;`, coma decimal), con BOM para que abra bien las tildes. */
export function csvPedido(pedido: Pedido): string {
  const s = pedido.conSaldos;
  const filas = [
    ["Fiduciante", "Participación (%)", "Parte justa (USD)", ...(s ? ["Saldo (USD)", "A cubrir (USD)"] : []), "A pedir (USD)"],
    ...pedido.filas.map((f) => [
      f.nombre,
      aTextoApi(f.porcentaje).replace(".", ","),
      numeroCsv(f.justo),
      ...(s ? [numeroCsv(f.saldo ?? "0.00"), numeroCsv(f.aCubrir)] : []),
      numeroCsv(f.pedido),
    ]),
    ["Total", "100", numeroCsv(pedido.objetivo), ...(s ? ["", numeroCsv(pedido.aCubrir)] : []), numeroCsv(pedido.total)],
  ];
  return "﻿" + filas.map((f) => f.map(celdaCsv).join(";")).join("\r\n") + "\r\n";
}

/** Lista para pegar en un mensaje: "Ana Gómez: USD 20.000", una por línea, y el total. */
export function textoPedido(pedido: Pedido): string {
  return [...pedido.filas.map((f) => `${f.nombre}: ${formatUsd(f.pedido)}`), `Total: ${formatUsd(pedido.total)}`].join("\n");
}

export interface PayloadLlamadoAMano extends PayloadLlamado {
  montos: { fiduciante_id: number; monto_usd: string }[];
}

/** El llamado con lo redondo de cada uno: monto = el total pedido, sin cubrir con ventas. Del form se usan fecha,
 * vencimiento y descripción. */
export function aPayloadLlamadoAMano(pedido: Pedido, form: FormLlamado): PayloadLlamadoAMano {
  return {
    fecha: form.fecha,
    vencimiento: form.vencimiento,
    monto_usd: pedido.total,
    descripcion: form.descripcion.trim() || null,
    cubierto_con_ventas_usd: "0",
    montos: pedido.filas.map((f) => ({ fiduciante_id: f.fiduciante_id, monto_usd: f.pedido })),
  };
}
